// ---------------------------------------------------------------------------
// ChannelsViewModeToggle — Table | Cards segmented control.
//
// Polish 12: migrated from a standalone <Button> pair to the Radix
// `ToggleGroup` primitive (same as the sparkline metric selector and
// the discovery/filters QualityToggle). Gives the whole Channels page
// a single toggle visual language.
// ---------------------------------------------------------------------------

import React from 'react';
import { LayoutList, LayoutGrid } from 'lucide-react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import type { ViewMode } from '@/components/v11/discovery/types';

export interface ChannelsViewModeToggleProps {
  readonly value: ViewMode;
  readonly onChange: (mode: ViewMode) => void;
}

type Option = {
  readonly value: ViewMode;
  readonly label: string;
  readonly Icon: typeof LayoutList;
};

const OPTIONS: readonly Option[] = [
  { value: 'table', label: 'Table', Icon: LayoutList },
  { value: 'chart', label: 'Cards', Icon: LayoutGrid },
];

const ARIA: Readonly<Record<ViewMode, string>> = {
  table: 'Switch to table view',
  chart: 'Switch to card view',
};

export function ChannelsViewModeToggle({ value, onChange }: ChannelsViewModeToggleProps) {
  // Radix `type="single"` can fire onValueChange('') when the active item
  // is re-clicked. Guard so the toggle never enters a "neither" state —
  // there is always exactly one view mode.
  const handleValueChange = (next: string) => {
    if (!next) return;
    onChange(next as ViewMode);
  };

  return (
    <ToggleGroup
      type="single"
      value={value}
      onValueChange={handleValueChange}
      aria-label="View mode"
      className="gap-0 border border-v11-border rounded-md overflow-hidden bg-v11-bg-secondary"
    >
      {OPTIONS.map(({ value: v, label, Icon }, i) => (
        <ToggleGroupItem
          key={v}
          value={v}
          aria-label={ARIA[v]}
          className={`data-[state=on]:bg-primary data-[state=on]:text-primary-foreground hover:bg-v11-surface-hover transition rounded-none text-xs font-medium gap-1.5 px-3 h-8 ${
            i < OPTIONS.length - 1 ? 'border-r border-v11-border/30' : ''
          }`}
        >
          <Icon className="h-4 w-4" aria-hidden />
          <span className="hidden sm:inline">{label}</span>
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
